import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataTable from 'react-data-table-component';

import { API_BASE_URL } from '../../config.js';
import FeedbackChatBox from './FeedbackChatBox';

const REASON_MAX_LENGTH = 1000;
// xin_chat_history.reason is varchar(200)
const MESSAGE_MAX_LENGTH = 200;

const EmployeeFeedback = ({ fixNavbar }) => {
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const emptyForm = {
    category_id: '',
    reason: '',
  };
  const [form, setForm] = useState(emptyForm);

  /* ================= CHAT STATE ================= */
  const [chatRow, setChatRow] = useState(null);
  const [chatThread, setChatThread] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
  };

  /* ================= FETCH DATA ================= */
  const loadCategories = () => {
    axios
      .get(API_BASE_URL + '/feedback_category_list', authHeader)
      .then((res) => setCategories(res.data || []))
      .catch(() => toast.error('Failed to load feedback categories'));
  };

  const loadData = () => {
    axios
      .get(API_BASE_URL + '/employee_feedback_list', authHeader)
      .then((res) => setData(res.data || []))
      .catch(() => toast.error('Failed to load feedback list'));
  };

  useEffect(() => {
    loadCategories();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= SEARCH ================= */
  const filteredData = data.filter((item) =>
    `${item.emp_code}${item.employee_name}${item.category_name}${item.reason}${item.applied_on}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ================= FORM HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReasonChange = (e) => {
    const { value } = e.target;
    if (value.length > REASON_MAX_LENGTH) {
      // toastId keeps every extra keystroke from stacking the same message
      toast.error(`Only ${REASON_MAX_LENGTH} characters allowed`, {
        toastId: 'feedback-length',
      });
      return;
    }
    setForm((prev) => ({ ...prev, reason: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // guard against double / rapid clicks while a save is already in flight
    if (savingRef.current) return;

    if (!form.category_id) {
      toast.error('Kindly select category', { toastId: 'feedback-validation' });
      return;
    }
    if (!form.reason.trim()) {
      toast.error('Kindly enter reason', { toastId: 'feedback-validation' });
      return;
    }

    savingRef.current = true;
    setSaving(true);
    axios
      .post(API_BASE_URL + '/employee_feedback_add', form, authHeader)
      .then((res) => {
        if (res.data && res.data.status === 200) {
          toast.success(res.data.message || 'Feedback saved successfully');
          setForm(emptyForm);
          loadData();
        } else {
          toast.error((res.data && res.data.message) || 'Failed to save', {
            toastId: 'feedback-save',
          });
        }
      })
      .catch(() => toast.error('Failed to save feedback', { toastId: 'feedback-save' }))
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
      });
  };

  /* ================= CHAT HANDLERS ================= */
  const loadThread = (id) => {
    setChatLoading(true);
    axios
      .get(API_BASE_URL + '/employee_feedback_chat/' + id, authHeader)
      .then((res) => setChatThread(res.data))
      .catch(() => {
        toast.error('Failed to load conversation');
        setChatThread(null);
      })
      .finally(() => setChatLoading(false));
  };

  const openChat = (row) => {
    setChatRow(row);
    setChatThread(null);
    setMessage('');
    loadThread(row.id);
  };

  const closeChat = () => {
    setChatRow(null);
    setChatThread(null);
    setMessage('');
  };

  const handleSend = (e) => {
    e.preventDefault();

    // guard against double / rapid clicks while a send is already in flight
    if (sendingRef.current) return;

    if (!message.trim()) {
      toast.error('Kindly enter the message', { toastId: 'feedback-chat' });
      return;
    }

    sendingRef.current = true;
    setSending(true);
    axios
      .post(
        API_BASE_URL + '/employee_feedback_chat_add',
        { feedback_id: chatRow.id, reason: message },
        authHeader
      )
      .then((res) => {
        if (res.data && res.data.status === 200) {
          setMessage('');
          loadThread(chatRow.id);
        } else {
          toast.error((res.data && res.data.message) || 'Failed to send', {
            toastId: 'feedback-chat',
          });
        }
      })
      .catch(() => toast.error('Failed to send message', { toastId: 'feedback-chat' }))
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  };

  /* ================= STATUS LABEL ================= */
  // The CFO writes their reply into cfo_status; the literal value "2" closes
  // the entry, anything else (including a reply) leaves it open.
  const statusBadge = (row) => {
    if (String(row.cfo_status) === '2')
      return <span className="badge badge-danger">Closed</span>;
    return <span className="badge badge-success">Open</span>;
  };

  /* ================= DATATABLE COLUMNS ================= */
  const columns = [
    {
      name: 'S.No',
      selector: (row, index) => index + 1,
      width: '80px',
    },
    {
      name: 'Chat',
      width: '80px',
      cell: (row) =>
        // a closed feedback has no chat button, same as the legacy module
        String(row.cfo_status) === '2' ? (
          <span className="text-muted">-</span>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-outline-info"
            title="Chat"
            onClick={() => openChat(row)}
          >
            <i className="fa fa-comments" />
          </button>
        ),
    },
    {
      name: 'Employee ID',
      selector: (row) => row.emp_code || '-',
      sortable: true,
      width: '130px',
    },
    {
      name: 'Employee Name',
      selector: (row) => row.employee_name || '-',
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '160px',
    },
    {
      name: 'Category',
      selector: (row) => row.category_name || '-',
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Reason',
      selector: (row) => row.reason || '',
      sortable: true,
      wrap: true,
      grow: 3,
      cell: (row) => {
        const reason = row.reason || '';
        const shortText =
          reason.length > 40 ? reason.substring(0, 40) + '...' : reason;
        return (
          <div className="remark-hover-wrapper">
            {shortText}
            <div className="remark-hover-box gp-scroll gp-left">{reason}</div>
          </div>
        );
      },
    },
    {
      name: 'CFO Status',
      selector: (row) => row.cfo_status,
      sortable: true,
      width: '120px',
      cell: (row) => statusBadge(row),
    },
    {
      name: 'Applied On',
      selector: (row) => row.applied_on || '-',
      sortable: true,
      minWidth: '130px',
    },
  ];

  return (
    <>
      <ToastContainer position="bottom-right" />
      <div className={`section-body ${fixNavbar ? 'marginTop' : ''}`}>
        <div className="container-fluid">
          {/* ================= ADD FEEDBACK ================= */}
          <div className="card">
            <div className="card-header">
              <h3
                className="card-title d-flex align-items-center mb-0"
                style={{ color: '#1d3a8a', fontWeight: 600 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 16 16"
                  fill="#1d3a8a"
                  style={{ marginRight: '8px', verticalAlign: '-3px' }}
                >
                  <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                </svg>
                Add Employee Feedback
              </h3>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 form-group">
                    <label>
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control required-input"
                      name="category_id"
                      value={form.category_id}
                      onChange={handleChange}
                    >
                      <option value="">Choose Feedback Category</option>
                      {categories.map((category) => (
                        <option
                          key={category.category_id}
                          value={category.category_id}
                        >
                          {category.category_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 form-group mb-0">
                    <label>
                      Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control required-input"
                      name="reason"
                      rows="5"
                      placeholder="Reason"
                      value={form.reason}
                      onChange={handleReasonChange}
                    />
                    <small className="text-muted">
                      {form.reason.length} / {REASON_MAX_LENGTH} characters
                    </small>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <i className="fa fa-check-square-o mr-1" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>

          {/* ================= LIST ================= */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3
                className="card-title d-flex align-items-center mb-0"
                style={{ color: '#1d3a8a', fontWeight: 600 }}
              >
                List All Feedback
              </h3>

              <div style={{ position: 'relative', width: '260px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingRight: '30px' }}
                />
                {searchTerm && (
                  <span
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      color: '#aaa',
                      fontSize: '18px',
                    }}
                  >
                    ✖
                  </span>
                )}
              </div>
            </div>

            <div className="card-body">
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                striped
                responsive
                persistTableHead
                noDataComponent="No feedback records found"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================= CHAT MODAL ================= */}
      {chatRow && (
        <>
          <div className="modal fade show d-block" role="dialog" tabIndex="-1">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Chat box</h5>
                  <button type="button" className="close" onClick={closeChat}>
                    <span>&times;</span>
                  </button>
                </div>

                <div className="modal-body">
                  <FeedbackChatBox
                    thread={chatThread}
                    loading={chatLoading}
                    viewer="employee"
                    footer={
                      chatThread && chatThread.feedback.is_closed ? (
                        <div className="efb-closed-note">
                          This feedback is closed.
                        </div>
                      ) : (
                        <form className="efb-composer" onSubmit={handleSend}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type your message..."
                            maxLength={MESSAGE_MAX_LENGTH}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={sending}
                          />
                          <button type="submit" className="btn" disabled={sending}>
                            {sending ? 'Sending...' : 'Send'}
                          </button>
                        </form>
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  fixNavbar: state.settings.isFixNavbar,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeFeedback);
